import {
  IToolboxComponent,
  ConfigurableFormItem,
  validateConfigurableComponentSettings,
  useGlobalState,
  useAuth,
  useForm,
  IConfigurableFormComponent,
  useConfigurableActionDispatcher,
  useAvailableConstantsData,
  IApplicationContext,
  useSheshaApplication,
} from "@shesha-io/reactjs";

import { IConfigurableActionConfiguration } from "@shesha-io/reactjs/dist/interfaces/configurableAction";
import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  TelephonyProvider,
  useTelephonyContext,
} from "../../components/newReactComponent/telephonyContext";
import ShaTelephony from "../../components/newReactComponent";
import { settingsForm } from "./settings";
import { PhoneOutlined } from "@ant-design/icons";
import { Alert } from "antd";

export enum TelephonyPermissions {
  Inbound = "Telephony-Inbound",
  Outbound = "Telephony-Outbound",
}

/**
 * Every setting is optional. IToolboxComponent<TModel> is invariant in TModel - Factory takes
 * it - so a component whose model declares required properties cannot be placed in an
 * IToolboxComponentGroup, whose members are IToolboxComponent<IConfigurableFormComponent>.
 * Optional settings make IConfigurableFormComponent assignable and the group typechecks with
 * no cast. It is also the truthful shape: these values come from designer-authored JSON.
 */
export interface IShaTelephonyProps extends IConfigurableFormComponent {
  name?: string;
  providerType?: "innovaphone" | "awsconnect" | "signalr";
  showDialpad?: boolean;
  enableRejectCall?: boolean;
  showAvailabilityToggle?: boolean;
  serverUrl?: string;
  hubUrl?: string;
  apiBaseUrl?: string;
  username?: string;
  password?: string;
  campaign?: string;
  skill?: string;
  vdn?: string;
  extension?: string;
  region?: string;
  instanceId?: string;
  instanceUrl?: string;
  onCallStarted?: IConfigurableActionConfiguration;
  onCallEnded?: IConfigurableActionConfiguration;
  onAgentStatusChanged?: IConfigurableActionConfiguration;
  onCallStatusChanged?: IConfigurableActionConfiguration;
  doCallTransfer?: IConfigurableActionConfiguration;
  contactFlowId?: string;
  defaultCountryCode?: string;
  allowOutbound?: boolean;
  allowInbound?: boolean;
}

const TelephonyWrapper: React.FC<IShaTelephonyProps> = (props) => {
  const {
    providerType,
    showDialpad,
    showAvailabilityToggle,
    serverUrl,
    hubUrl,
    apiBaseUrl,
    username,
    password,
    campaign,
    skill,
    vdn,
    extension,
    region,
    instanceId,
    instanceUrl,
    onCallStarted,
    onCallEnded,
    onAgentStatusChanged,
    doCallTransfer,
    onCallStatusChanged,
    enableRejectCall,
    contactFlowId,
    defaultCountryCode,
    allowInbound = true,
    allowOutbound = true,
  } = props;
  const { provider, providerState, initialize, getTelephonyAgent, isConnectionReady } = useTelephonyContext();
  const [agentFlags, setAgentFlags] = useState<{ hasInbound: boolean; hasOutbound: boolean } | null>(null);
  const { loginInfo } = useAuth();
  const { setState } = useGlobalState();
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  // Synchronous re-entrancy guard: setIsInitialized is async, so without this a
  // burst of re-renders could kick off initialize() (and construct a provider) twice.
  const initializingRef = useRef(false);

  const { anyOfPermissionsGranted } = useSheshaApplication();

  const { formMode } = useForm();

  interface IApplicationContextWithDetails extends IApplicationContext {
    callDetails?: any;
    agentStatus?: any;
    callStatus?: any;
    transferDetails?: any;
  }

  // Get the currently logged in user
  const currentUser = loginInfo;

  useEffect(() => {
    const initializeTelephony = async () => {

      if (formMode === 'designer') {
        console.log('In designer mode - skipping telephony initialization');
        return;
      }
      console.log("Initializing telephony with provider:", providerType);
      console.log("Initializing telephony with isInitialized:", isInitialized);
      // Wait for the SignalR connection before constructing a provider — the base
      // provider subscribes to it in its constructor. Not gating here is the race
      // that caused intermittent "Error initializing telephony" on load.
      if (isInitialized || initializingRef.current || !currentUser || !isConnectionReady)
        return;

      initializingRef.current = true;

      try {
        // First check if user has a telephony profile
        //const userFullname = currentUser.fullName;
        // let telephonyAgents = await getTelephonyAgent(
        //   `${backendUrl}/api/dynamic/Shesha.Telephony/TelephonyAgent/Crud/GetAll?quickSearch=${userFullname}`
        //   , httpHeaders
        // );
        // if (telephonyAgents?.length) {
        //   for (let i = 0; i < telephonyAgents.length; i++) {
        //     let agent = telephonyAgents[i];
        //     if (agent.person.id == currentUser.personId) {
        //       profile = agent;
        //     }
        //   }
        // }

        let config: any = null;

        // if (profile && providerType === 'innovaphone') {
        //   config = {
        //     ...profile.providerConfig, // deon - check this
        //     username: profile.username,
        //     password: profile.password,
        //   };
        // }

        // deon - test the below
        if (!config) {
          if (providerType === "innovaphone") {
            config = {
              serverUrl,
              username,
              password,
              campaign,
              skill,
              vdn,
              extension,
              defaultCountryCode,
            };
          } else if (providerType === "awsconnect") {
            config = {
              instanceId,
              instanceUrl,
              region,
              hubUrl,
              apiBaseUrl,
              contactFlowId,
              defaultCountryCode,
            };
          } else if (providerType === "signalr") {
            config = {
              hubUrl,
              apiBaseUrl,
            };
          }
        }

        if (config) {
          const success = await initialize(providerType, config);
          if (success) {
            // Only mark initialized on success, so a failed attempt retries when
            // the effect's dependencies (e.g. the connection) next change.
            setIsInitialized(true);
            // ISetStatePayload is { key: string; data?: unknown }. This call never supplied a
            // key, so it wrote nothing - telephonyInitialized is read nowhere in the repo.
            // Split into two keyed writes, matching telephonyContext.tsx.
            setState({ key: "telephonyInitialized", data: true });
            setState({ key: "telephonyProvider", data: providerType });

          } else {
            setError("Failed to initialize telephony provider");
          }
        } else {
          setError("No telephony configuration available");
        }
      } catch (error) {
        console.error("Error initializing telephony:", error);
        setError("An error occurred while initializing telephony");
      } finally {
        initializingRef.current = false;
      }
    };

    initializeTelephony();

    // Cleanup on unmount
    return () => {
      if (provider && providerState.isLoggedIn) {
        // provider.logout().then(() => {
        //   setState({ data: { telephonyInitialized: false, telephonyProvider: null } });
        // });
      }
    };
  }, [
    currentUser,
    isConnectionReady,
    initialize,
    providerType,
    provider,
    //providerState.isLoggedIn,
    isInitialized,
    serverUrl,
    username,
    password,
    campaign,
    skill,
    vdn,
    extension,
    region,
    instanceUrl,
    contactFlowId,
    defaultCountryCode,
    setState,
  ]);

  // Load the user's TelephonyAgent flags for providers that don't show a popout
  // (e.g. Innovaphone). Providers like AWS Connect handle their own permissions.
  useEffect(() => {
    if (!provider || provider.supportsPopout) return undefined;
    let cancelled = false;
    (async () => {
      const agent = await getTelephonyAgent();
      if (cancelled || !agent) return;
      setAgentFlags({
        hasInbound: !!agent.hasInbound,
        hasOutbound: !!agent.hasOutbound,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [provider, getTelephonyAgent]);

  const { executeAction } = useConfigurableActionDispatcher();

  const allData = useAvailableConstantsData();

  const evaluationContext2 = {
    ...allData,
  } as IApplicationContextWithDetails;

  const handleCallStarted = useCallback(
    (callDetails: any) => {
      console.log("Call started in compoenent:", callDetails);
      console.log("onCallStarted", onCallStarted);

      if (onCallStarted) {
        //const context = { data: formData, callDetails };

        evaluationContext2.callDetails = callDetails;

        executeAction({
          actionConfiguration: onCallStarted,
          argumentsEvaluationContext: evaluationContext2,
        });

        // if (typeof onCallStarted === "string") {
        //   executeScript(onCallStarted, context);
        // } else if (typeof onCallStarted === "function") {
        //   onCallStarted(context);
        // } else if (onCallStarted.script) {
        //   executeScript(onCallStarted.script, context);
        // } else if (onCallStarted.actionName) {
        //   // Handle configurable action
        //   const actionContext = {
        //     data: formData,
        //     mode: formMode,
        //     callDetails,
        //   };

        //   evaluateValue(onCallStarted, actionContext);
        // }
      }
    },
    [onCallStarted]
  );

  const handleCallEnded = useCallback(
    (callDetails: any) => {
      if (onCallEnded) {
        //const context = { data: formData, callDetails };

        console.log("Call ended in component:", callDetails);
        console.log("onCallEnded", onCallEnded);
        evaluationContext2.callDetails = callDetails;

        executeAction({
          actionConfiguration: onCallEnded,
          argumentsEvaluationContext: evaluationContext2,
        });
      }
    },
    [onCallEnded]
  );

  const handleOnAgentStatusChanged = useCallback(
    (agentStatus: any) => {
      console.log("Agent status changed in design component:", agentStatus);
      console.log("onAgentStatusChanged in design", onAgentStatusChanged);
      if (onAgentStatusChanged) {
        //const context = { data: formData, agentStatus };

        console.log("Agent status changed in component:", agentStatus);
        console.log("onAgentStatusChanged", onAgentStatusChanged);
        evaluationContext2.agentStatus = agentStatus;

        executeAction({
          actionConfiguration: onAgentStatusChanged,
          argumentsEvaluationContext: evaluationContext2,
        });
      }
    },
    [onAgentStatusChanged]
  );

  const handleOnCallStatusChanged = useCallback(
    (callStatus: any) => {
      console.log("Call status changed in design component:", callStatus);
      console.log("onCallStatusChanged in design", onCallStatusChanged);
      if (onCallStatusChanged) {
        //const context = { data: formData, callStatus };

        console.log("Call status changed in component:", callStatus);
        console.log("onCallStatusChanged", onCallStatusChanged);
        evaluationContext2.callStatus = callStatus;

        executeAction({
          actionConfiguration: onCallStatusChanged,
          argumentsEvaluationContext: evaluationContext2,
        });
      }
    },
    [onCallStatusChanged]
  );


  const handleDoCallTransfer = useCallback(
    (transferDetails: any) => {
      console.log("Transfer call initiated in design component:", transferDetails);
      console.log("doCallTransfer in design", doCallTransfer);
      if (doCallTransfer) {
        console.log('doCallTransfer', doCallTransfer);
        //const context = { data: formData, transferDetails };

        console.log("Transfer details changed in component:", transferDetails);

        evaluationContext2.transferDetails = transferDetails;

        executeAction({
          actionConfiguration: doCallTransfer,
          argumentsEvaluationContext: evaluationContext2,
        });
      }
    },
    [doCallTransfer]
  );



  return (
    <>
      {error && <Alert message={error} type="error" showIcon closable />}

      <ShaTelephony
        providerType={providerType}
        showDialpad={showDialpad}
        showAvailabilityToggle={showAvailabilityToggle}
        onCallStarted={handleCallStarted}
        onCallEnded={handleCallEnded}
        onAgentStatusChanged={handleOnAgentStatusChanged}
        onCallStatusChanged={handleOnCallStatusChanged}
        doCallTransfer={doCallTransfer && doCallTransfer.actionName ? handleDoCallTransfer : undefined}
        enableRejectCall={enableRejectCall}
        allowOutbound={
          allowOutbound &&
          anyOfPermissionsGranted([TelephonyPermissions.Outbound]) &&
          (provider?.supportsPopout !== false || (agentFlags !== null && agentFlags.hasOutbound))
        }
        allowInbound={
          allowInbound &&
          anyOfPermissionsGranted([TelephonyPermissions.Inbound]) &&
          (provider?.supportsPopout !== false || (agentFlags !== null && agentFlags.hasInbound))
        }
      />
    </>
  );
};

const ShaTelephonyToolboxComponent: IToolboxComponent<IShaTelephonyProps> = {
  type: "ShaTelephony",
  name: "Telephony Integration",
  icon: <PhoneOutlined />,
  isInput: false,
  isHidden: false,
  // category: "Communication",
  // group: "Components",
  // description: "Integrates with telephony providers (Innovaphone, AWS Connect) to enable call functionality",

  Factory: ({ model }) => {
    return (
      <ConfigurableFormItem model={model}>
        {() => (
          <TelephonyProvider>
            <TelephonyWrapper {...model} />
          </TelephonyProvider>
        )}
      </ConfigurableFormItem>
    );
  },

  settingsFormMarkup: settingsForm,
  validateSettings: (model) =>
    validateConfigurableComponentSettings(settingsForm, model),

  initModel: (model) => {
    return {
      ...model,
      name: "telephony",
      providerType: "innovaphone",
      showDialpad: true,
      showAvailabilityToggle: true,
      enableRejectCall: true,
      // moved from the settings form: 0.46.0 removed defaultValue from the settings types.
      allowOutbound: true,
      allowInbound: true,
    };
  },
};

export default ShaTelephonyToolboxComponent;
